import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Switch,
  ActivityIndicator,
  SafeAreaView,
  Alert,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
// Instead of using DateTimePicker, we'll use direct text input for dates on all platforms
// This avoids platform-specific issues
import {
  OpportunityType,
  GradeLevel,
  OpportunityStatus,
  InteractionType,
} from "../../types";
import {
  getOpportunityById,
  createOpportunity,
  updateOpportunity,
} from "../../services/parseService";
import { format, parseISO } from "date-fns";

interface OpportunityEditorScreenProps {
  opportunityId: string | null; // null for new opportunity
  onClose: () => void;
}

const OpportunityEditorScreen: React.FC<OpportunityEditorScreenProps> = ({
  opportunityId,
  onClose,
}) => {
  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [types, setTypes] = useState<OpportunityType[]>([]);
  const [organization, setOrganization] = useState("");
  const [location, setLocation] = useState("");
  const [website, setWebsite] = useState("");
  const [eligibility, setEligibility] = useState("");
  const [cost, setCost] = useState("");
  const [isYearlyRecurring, setIsYearlyRecurring] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [gradeLevels, setGradeLevels] = useState<GradeLevel[]>([]);
  const [interactionTypes, setInteractionTypes] = useState<InteractionType[]>(
    [],
  );
  const [isClaimed, setIsClaimed] = useState(false);

  // Date states with string representation for display
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [startDateString, setStartDateString] = useState("");
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [endDateString, setEndDateString] = useState("");
  const [applicationOpenDate, setApplicationOpenDate] = useState<Date>(
    new Date(),
  );
  const [applicationOpenDateString, setApplicationOpenDateString] =
    useState("");
  const [applicationCloseDate, setApplicationCloseDate] = useState<Date>(
    new Date(),
  );
  const [applicationCloseDateString, setApplicationCloseDateString] =
    useState("");

  // We're using direct string inputs for dates instead of DatePicker

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Options
  const opportunityTypes: OpportunityType[] = [
    "Competition",
    "Grant",
    "Scholarship",
    "Hackathon",
    "Internship",
    "Workshop/Programs",
    "Conference",
    "Other",
  ];

  const gradeOptions: GradeLevel[] = [
    "Elementary",
    "Middle School",
    "High School",
    "Undergraduate",
    "Graduate",
    "Professional",
    "All Levels",
  ];

  const interactionOptions: InteractionType[] = [
    "In-Person",
    "Virtual",
    "Hybrid",
  ];

  // Load opportunity data if editing
  useEffect(() => {
    if (opportunityId) {
      loadOpportunity();
    }
  }, [opportunityId]);

  const loadOpportunity = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const opportunity = await getOpportunityById(opportunityId!);

      if (!opportunity) {
        throw new Error("Opportunity not found");
      }

      // Extract attributes
      const attrs = opportunity.attributes || opportunity;

      // Helper to parse dates and format to MM/DD/YYYY for the text inputs
      const parseDate = (dateObj: any): { date: Date; formatted: string } => {
        if (!dateObj) return { date: new Date(), formatted: "" };

        let date: Date;

        if (dateObj instanceof Date) {
          date = dateObj;
        } else if (dateObj.iso) {
          date = new Date(dateObj.iso);
        } else if (typeof dateObj === "string") {
          date = new Date(dateObj);
        } else {
          return { date: new Date(), formatted: "" };
        }

        if (isNaN(date.getTime())) return { date: new Date(), formatted: "" };

        // Format as MM/DD/YYYY for text input
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear();

        return {
          date,
          formatted: `${month}/${day}/${year}`,
        };
      };

      // Set form values
      setTitle(attrs.title || "");
      setDescription(attrs.description || "");

      // Handle types - can be either string or array
      if (Array.isArray(attrs.type)) {
        setTypes(attrs.type);
      } else if (attrs.type) {
        setTypes([attrs.type as OpportunityType]);
      } else {
        setTypes([]);
      }

      setOrganization(attrs.organization || "");
      setLocation(attrs.location || "");
      setWebsite(attrs.website || "");
      setEligibility(attrs.eligibility || "");
      setCost(attrs.cost || "");
      setIsYearlyRecurring(!!attrs.isYearlyRecurring);
      setIsClaimed(!!attrs.isClaimed);
      setTags(Array.isArray(attrs.tags) ? attrs.tags : []);
      setGradeLevels(
        Array.isArray(attrs.gradeLevel)
          ? (attrs.gradeLevel as GradeLevel[])
          : [],
      );
      setInteractionTypes(
        Array.isArray(attrs.interactionTypes)
          ? (attrs.interactionTypes as InteractionType[])
          : [],
      );

      // Set dates
      if (attrs.startDate) {
        const parsed = parseDate(attrs.startDate);
        setStartDate(parsed.date);
        setStartDateString(parsed.formatted);
      }

      if (attrs.endDate) {
        const parsed = parseDate(attrs.endDate);
        setEndDate(parsed.date);
        setEndDateString(parsed.formatted);
      }

      if (attrs.applicationOpenDate) {
        const parsed = parseDate(attrs.applicationOpenDate);
        setApplicationOpenDate(parsed.date);
        setApplicationOpenDateString(parsed.formatted);
      }

      if (attrs.applicationCloseDate) {
        const parsed = parseDate(attrs.applicationCloseDate);
        setApplicationCloseDate(parsed.date);
        setApplicationCloseDateString(parsed.formatted);
      }
    } catch (err) {
      console.error("Error loading opportunity:", err);
      setError("Failed to load opportunity details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    console.log("🔄 HandleSave called", { opportunityId, title, organization, location, types });
    
    // Basic validation with better cross-platform alerts
    if (!title.trim()) {
      const message = "Please enter a title for the opportunity.";
      if (Platform.OS === 'web') {
        alert(message);
      } else {
        Alert.alert("Missing Information", message);
      }
      return;
    }

    if (!organization.trim()) {
      const message = "Please enter the organization name.";
      if (Platform.OS === 'web') {
        alert(message);
      } else {
        Alert.alert("Missing Information", message);
      }
      return;
    }

    if (!location.trim()) {
      const message = "Please enter a location.";
      if (Platform.OS === 'web') {
        alert(message);
      } else {
        Alert.alert("Missing Information", message);
      }
      return;
    }

    if (types.length === 0) {
      const message = "Please select at least one opportunity type.";
      if (Platform.OS === 'web') {
        alert(message);
      } else {
        Alert.alert("Missing Information", message);
      }
      return;
    }

    try {
      console.log("✅ Validation passed, setting saving state");
      setIsSaving(true);

      // Prepare opportunity data
      const opportunityData: any = {
        title: title.trim(),
        description: description.trim(),
        type: types.length === 1 ? types[0] : types.join(", "),
        organization: organization.trim(),
        location: location.trim(),
        eligibility: eligibility.trim(),
        website: website.trim(),
        tags,
        gradeLevel: gradeLevels,
        interactionTypes: interactionTypes.length > 0 ? interactionTypes : undefined,
        isYearlyRecurring,
        isClaimed,
        cost: cost.trim(),
        status: calculateStatus() as OpportunityStatus,
      };

      // Handle dates properly for both create and update operations
      if (opportunityId) {
        // For updates: explicitly set dates to null if cleared, or to the date value if provided
        opportunityData.startDate = startDateString.trim() ? startDate : null;
        opportunityData.endDate = endDateString.trim() ? endDate : null;
        opportunityData.applicationOpenDate = applicationOpenDateString.trim() ? applicationOpenDate : null;
        opportunityData.applicationCloseDate = applicationCloseDateString.trim() ? applicationCloseDate : null;
      } else {
        // For new opportunities: only include dates if provided
        if (startDateString.trim()) opportunityData.startDate = startDate;
        if (endDateString.trim()) opportunityData.endDate = endDate;
        if (applicationOpenDateString.trim()) opportunityData.applicationOpenDate = applicationOpenDate;
        if (applicationCloseDateString.trim()) opportunityData.applicationCloseDate = applicationCloseDate;
      }

      console.log("📝 Prepared opportunity data:", opportunityData);

      // Create or update opportunity
      if (opportunityId) {
        console.log("📝 Updating existing opportunity:", opportunityId);
        const result = await updateOpportunity(opportunityId, opportunityData);
        console.log("✅ Update result:", result);
        
        const successMessage = "Opportunity updated successfully!";
        if (Platform.OS === 'web') {
          alert(successMessage);
        } else {
          Alert.alert("Success", successMessage);
        }
      } else {
        console.log("📝 Creating new opportunity");
        const result = await createOpportunity(opportunityData);
        console.log("✅ Create result:", result);
        
        const successMessage = "New opportunity created successfully!";
        if (Platform.OS === 'web') {
          alert(successMessage);
        } else {
          Alert.alert("Success", successMessage);
        }
      }

      console.log("🎉 Save operation completed successfully, closing editor");
      onClose();
    } catch (err) {
      console.error("❌ Error saving opportunity:", err);
      
      const errorMessage = err instanceof Error ? err.message : String(err);
      const fullErrorMessage = `Failed to save opportunity: ${errorMessage}`;
      
      if (Platform.OS === 'web') {
        alert(fullErrorMessage);
      } else {
        Alert.alert("Error", fullErrorMessage);
      }
    } finally {
      console.log("🔄 Setting saving state to false");
      setIsSaving(false);
    }
  };

  const calculateStatus = (): OpportunityStatus => {
    const now = new Date();

    // Case 1: Applications are open (highest priority)
    if (applicationOpenDateString && applicationCloseDateString) {
      if (now >= applicationOpenDate && now <= applicationCloseDate) {
        return "Open";
      }
    }

    // Case 2: Coming Soon - if we have start date and it's in the future
    if (startDateString && now < startDate) {
      return "Coming Soon";
    }

    // Case 3: No date information at all - default to "Open" (more accessible)
    if (!startDateString && !endDateString && !applicationOpenDateString && !applicationCloseDateString) {
      return "Open";
    }

    // Case 4: Incomplete program dates (only start date) - default to "Open"
    if (startDateString && !endDateString && !applicationOpenDateString && !applicationCloseDateString) {
      return "Open";
    }

    // Case 5: Has both program dates but no application dates
    if (startDateString && endDateString && !applicationOpenDateString && !applicationCloseDateString) {
      // If it's in the future, show as "Coming Soon"
      if (now < startDate) {
        return "Coming Soon";
      }
      // If it's ongoing or past, but no specific application info, default to "Open"
      return "Open";
    }

    // Case 6: Default fallback - if applications have closed and program is past
    return "Closed";
  };

  // Parse a date string in MM/DD/YYYY format
  const parseCustomDateString = (dateString: string): Date | null => {
    if (!dateString.trim()) return null;

    // Accept both MM/DD/YYYY and M/D/YYYY formats
    const dateParts = dateString.split("/");
    if (dateParts.length !== 3) return null;

    const month = parseInt(dateParts[0], 10) - 1; // Months are 0-indexed
    const day = parseInt(dateParts[1], 10);
    const year = parseInt(dateParts[2], 10);

    if (isNaN(month) || isNaN(day) || isNaN(year)) return null;

    const date = new Date(year, month, day);
    return date;
  };

  // Update both the Date object and the string value
  const handleDateInput = (
    dateString: string,
    dateType: "start" | "end" | "appOpen" | "appClose",
  ) => {
    const date = parseCustomDateString(dateString);

    switch (dateType) {
      case "start":
        setStartDateString(dateString);
        if (date) setStartDate(date);
        break;
      case "end":
        setEndDateString(dateString);
        if (date) setEndDate(date);
        break;
      case "appOpen":
        setApplicationOpenDateString(dateString);
        if (date) setApplicationOpenDate(date);
        break;
      case "appClose":
        setApplicationCloseDateString(dateString);
        if (date) setApplicationCloseDate(date);
        break;
    }
  };

  const toggleGradeLevel = (level: GradeLevel) => {
    if (gradeLevels.includes(level)) {
      setGradeLevels(gradeLevels.filter((gl) => gl !== level));
    } else {
      setGradeLevels([...gradeLevels, level]);
    }
  };

  const toggleType = (optionType: OpportunityType) => {
    if (types.includes(optionType)) {
      setTypes(types.filter((t) => t !== optionType));
    } else {
      setTypes([...types, optionType]);
    }
  };

  const toggleInteractionType = (type: InteractionType) => {
    if (interactionTypes.includes(type)) {
      setInteractionTypes(interactionTypes.filter((t) => t !== type));
    } else {
      setInteractionTypes([...interactionTypes, type]);
    }
  };

  const addTag = () => {
    if (tagInput.trim() !== "" && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  if (isLoading) {
    return (
      <View style={styles.fullScreenModal}>
        <SafeAreaView style={styles.container}>
          <View style={styles.header}>
            <Text style={styles.headerTitle}>
              {opportunityId ? "Edit Opportunity" : "Create Opportunity"}
            </Text>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <Ionicons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#2563EB" />
            <Text style={styles.loadingText}>Loading opportunity details...</Text>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.fullScreenModal}>
        <SafeAreaView style={styles.container}>
          <View style={styles.header}>
            <Text style={styles.headerTitle}>
              {opportunityId ? "Edit Opportunity" : "Create Opportunity"}
            </Text>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <Ionicons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          <View style={styles.errorContainer}>
            <Ionicons name="alert-circle-outline" size={48} color="#DC2626" />
            <Text style={styles.errorTitle}>Error Loading Data</Text>
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity
              style={styles.retryButton}
              onPress={opportunityId ? loadOpportunity : onClose}
              activeOpacity={0.7}
            >
              <Text style={styles.retryButtonText}>
                {opportunityId ? "Try Again" : "Go Back"}
              </Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  return (
    <View style={styles.fullScreenModal}>
      <SafeAreaView style={styles.container}>
        {/* Clean Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <TouchableOpacity style={styles.backButton} onPress={onClose}>
              <Ionicons name="arrow-back" size={20} color="#6B7280" />
            </TouchableOpacity>
            <View style={styles.headerTitleContainer}>
              <Text style={styles.headerTitle}>
                {opportunityId ? "Edit Opportunity" : "Create Opportunity"}
              </Text>
              <Text style={styles.headerSubtitle}>
                {opportunityId ? "Update opportunity details" : "Fill in the details below"}
              </Text>
            </View>
          </View>
          <View style={styles.headerActions}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={onClose}
              activeOpacity={0.7}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.saveButton, isSaving && styles.savingButton]}
              onPress={handleSave}
              disabled={isSaving}
              activeOpacity={0.7}
            >
              {isSaving ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <>
                  <Ionicons name="checkmark" size={16} color="#FFFFFF" />
                  <Text style={styles.saveButtonText}>
                    {opportunityId ? "Update" : "Create"}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>

        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          {/* Basic Information Section */}
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Basic Information</Text>

            <Text style={styles.label}>Title*</Text>
            <TextInput
              style={styles.textInput}
              value={title}
              onChangeText={setTitle}
              placeholder="Enter opportunity title"
              placeholderTextColor="#9CA3AF"
            />

            <Text style={styles.label}>Organization*</Text>
            <TextInput
              style={styles.textInput}
              value={organization}
              onChangeText={setOrganization}
              placeholder="Enter organization name"
              placeholderTextColor="#9CA3AF"
            />

            <Text style={styles.label}>Type* (Select all that apply)</Text>
            <View style={styles.optionsContainer}>
              {opportunityTypes.map((optionType) => (
                <TouchableOpacity
                  key={optionType}
                  style={[
                    styles.optionButton,
                    types.includes(optionType) && styles.optionButtonSelected,
                  ]}
                  onPress={() => toggleType(optionType)}
                  activeOpacity={0.7}
                >
                  <Text
                    style={[
                      styles.optionText,
                      types.includes(optionType) && styles.optionTextSelected,
                    ]}
                  >
                    {optionType}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={styles.label}>Location*</Text>
            <TextInput
              style={styles.textInput}
              value={location}
              onChangeText={setLocation}
              placeholder="Enter location (city, online, etc.)"
              placeholderTextColor="#9CA3AF"
            />

            <Text style={styles.label}>Website</Text>
            <TextInput
              style={styles.textInput}
              value={website}
              onChangeText={setWebsite}
              placeholder="Enter website URL"
              placeholderTextColor="#9CA3AF"
              keyboardType="url"
              autoCapitalize="none"
            />

            <Text style={styles.label}>Description*</Text>
            <TextInput
              style={styles.textAreaInput}
              value={description}
              onChangeText={setDescription}
              placeholder="Enter detailed description"
              placeholderTextColor="#9CA3AF"
              multiline
              numberOfLines={6}
              textAlignVertical="top"
            />
          </View>

          {/* Dates Section */}
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Dates</Text>

            <Text style={styles.label}>Program/Event Dates* (MM/DD/YYYY)</Text>
            <View style={styles.dateRow}>
              <View style={styles.dateInputContainer}>
                <Ionicons name="calendar-outline" size={20} color="#6B7280" />
                <TextInput
                  style={styles.dateInput}
                  value={startDateString}
                  onChangeText={(text) => handleDateInput(text, "start")}
                  placeholder="Start Date"
                  placeholderTextColor="#9CA3AF"
                  keyboardType="numbers-and-punctuation"
                />
              </View>

              <View style={styles.dateInputContainer}>
                <Ionicons name="calendar-outline" size={20} color="#6B7280" />
                <TextInput
                  style={styles.dateInput}
                  value={endDateString}
                  onChangeText={(text) => handleDateInput(text, "end")}
                  placeholder="End Date"
                  placeholderTextColor="#9CA3AF"
                  keyboardType="numbers-and-punctuation"
                />
              </View>
            </View>

            <Text style={styles.label}>Application Period (MM/DD/YYYY)</Text>
            <View style={styles.dateRow}>
              <View style={styles.dateInputContainer}>
                <Ionicons name="calendar-outline" size={20} color="#6B7280" />
                <TextInput
                  style={styles.dateInput}
                  value={applicationOpenDateString}
                  onChangeText={(text) => handleDateInput(text, "appOpen")}
                  placeholder="Opens"
                  placeholderTextColor="#9CA3AF"
                  keyboardType="numbers-and-punctuation"
                />
              </View>

              <View style={styles.dateInputContainer}>
                <Ionicons name="calendar-outline" size={20} color="#6B7280" />
                <TextInput
                  style={styles.dateInput}
                  value={applicationCloseDateString}
                  onChangeText={(text) => handleDateInput(text, "appClose")}
                  placeholder="Closes"
                  placeholderTextColor="#9CA3AF"
                  keyboardType="numbers-and-punctuation"
                />
              </View>
            </View>

            <View style={styles.switchRow}>
              <Text style={styles.switchLabel}>Yearly Recurring Event</Text>
              <Switch
                value={isYearlyRecurring}
                onValueChange={setIsYearlyRecurring}
                trackColor={{ false: "#E5E7EB", true: "#DBEAFE" }}
                thumbColor={isYearlyRecurring ? "#2563EB" : "#FFFFFF"}
              />
            </View>
          </View>

          {/* Eligibility & Details Section */}
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Eligibility & Details</Text>

            <Text style={styles.label}>Grade Levels</Text>
            <View style={styles.optionsContainer}>
              {gradeOptions.map((level) => (
                <TouchableOpacity
                  key={level}
                  style={[
                    styles.optionButton,
                    gradeLevels.includes(level) && styles.optionButtonSelected,
                  ]}
                  onPress={() => toggleGradeLevel(level)}
                  activeOpacity={0.7}
                >
                  <Text
                    style={[
                      styles.optionText,
                      gradeLevels.includes(level) && styles.optionTextSelected,
                    ]}
                  >
                    {level}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={styles.label}>Interaction Type</Text>
            <View style={styles.optionsContainer}>
              {interactionOptions.map((type) => (
                <TouchableOpacity
                  key={type}
                  style={[
                    styles.optionButton,
                    interactionTypes.includes(type) && styles.optionButtonSelected,
                  ]}
                  onPress={() => toggleInteractionType(type)}
                  activeOpacity={0.7}
                >
                  <Text
                    style={[
                      styles.optionText,
                      interactionTypes.includes(type) && styles.optionTextSelected,
                    ]}
                  >
                    {type}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={styles.label}>Eligibility Requirements</Text>
            <TextInput
              style={styles.textAreaInput}
              value={eligibility}
              onChangeText={setEligibility}
              placeholder="Enter eligibility criteria"
              placeholderTextColor="#9CA3AF"
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />

            <Text style={styles.label}>Cost</Text>
            <TextInput
              style={styles.textInput}
              value={cost}
              onChangeText={setCost}
              placeholder="Enter cost information (e.g., Free, $50, etc.)"
              placeholderTextColor="#9CA3AF"
            />

            <Text style={styles.label}>Tags</Text>
            <View style={styles.tagInputContainer}>
              <TextInput
                style={styles.tagInput}
                value={tagInput}
                onChangeText={setTagInput}
                placeholder="Add tags"
                placeholderTextColor="#9CA3AF"
                onSubmitEditing={addTag}
              />
              <TouchableOpacity style={styles.addTagButton} onPress={addTag}>
                <Ionicons name="add" size={20} color="#FFFFFF" />
              </TouchableOpacity>
            </View>

            <View style={styles.tagsContainer}>
              {tags.map((tag) => (
                <View key={tag} style={styles.tag}>
                  <Text style={styles.tagText}>{tag}</Text>
                  <TouchableOpacity onPress={() => removeTag(tag)}>
                    <Ionicons name="close-circle" size={16} color="#6B7280" />
                  </TouchableOpacity>
                </View>
              ))}
            </View>

            <View style={styles.switchRow}>
              <Text style={styles.switchLabel}>Verified by Organization</Text>
              <Switch
                value={isClaimed}
                onValueChange={setIsClaimed}
                trackColor={{ false: "#E5E7EB", true: "#DBEAFE" }}
                thumbColor={isClaimed ? "#2563EB" : "#FFFFFF"}
              />
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  fullScreenModal: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#F9FAFB",
    zIndex: 1000,
  },
  container: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },
  
  // Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  headerTitleContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#111827",
    fontFamily: "Inter-SemiBold",
  },
  headerSubtitle: {
    fontSize: 14,
    color: "#6B7280",
    fontFamily: "Inter-Regular",
    marginTop: 2,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    marginRight: 12,
  },
  cancelButtonText: {
    fontSize: 14,
    color: "#6B7280",
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  saveButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#2563EB",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  savingButton: {
    backgroundColor: "#9CA3AF",
  },
  saveButtonText: {
    fontSize: 14,
    color: "#FFFFFF",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
    marginLeft: 4,
  },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },

  // Content
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
  },
  formSection: {
    marginBottom: 24,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#111827",
    marginBottom: 20,
    fontFamily: "Inter-SemiBold",
  },
  label: {
    fontSize: 14,
    fontWeight: "500",
    color: "#374151",
    marginBottom: 8,
    fontFamily: "Inter-Medium",
  },
  textInput: {
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: "#111827",
    marginBottom: 16,
    fontFamily: "Inter-Regular",
  },
  textAreaInput: {
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: "#111827",
    marginBottom: 16,
    minHeight: 100,
    fontFamily: "Inter-Regular",
  },

  // Options
  optionsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 16,
    marginHorizontal: -4,
  },
  optionButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
    marginHorizontal: 4,
    marginBottom: 8,
  },
  optionButtonSelected: {
    backgroundColor: "#2563EB",
    borderColor: "#2563EB",
  },
  optionText: {
    color: "#6B7280",
    fontSize: 14,
    fontFamily: "Inter-Medium",
  },
  optionTextSelected: {
    color: "#FFFFFF",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Dates
  dateRow: {
    flexDirection: "row",
    marginBottom: 16,
    marginHorizontal: -6,
  },
  dateInputContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginHorizontal: 6,
  },
  dateInput: {
    flex: 1,
    fontSize: 16,
    color: "#111827",
    marginLeft: 8,
    fontFamily: "Inter-Regular",
  },

  // Switch
  switchRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginVertical: 8,
  },
  switchLabel: {
    fontSize: 16,
    color: "#374151",
    fontFamily: "Inter-Regular",
  },

  // Tags
  tagInputContainer: {
    flexDirection: "row",
    marginBottom: 12,
  },
  tagInput: {
    flex: 1,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: "#111827",
    marginRight: 8,
    fontFamily: "Inter-Regular",
  },
  addTagButton: {
    backgroundColor: "#2563EB",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  tagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 16,
    marginHorizontal: -2,
  },
  tag: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#EEF2FF",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginHorizontal: 2,
    marginBottom: 4,
  },
  tagText: {
    fontSize: 12,
    color: "#2563EB",
    marginRight: 4,
    fontFamily: "Inter-Medium",
  },

  // Loading & Error States
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  loadingText: {
    marginTop: 12,
    color: "#6B7280",
    fontSize: 16,
    fontFamily: "Inter-Medium",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#111827",
    marginTop: 16,
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
  },
  errorText: {
    fontSize: 16,
    color: "#6B7280",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 24,
    fontFamily: "Inter-Regular",
  },
  retryButton: {
    backgroundColor: "#2563EB",
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },
  retryButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
});

export default OpportunityEditorScreen;

